import * as React from "react";
import { cn } from "@/lib/utils";

export interface CurrencyInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onChange" | "value" | "type"
  > {
  value: number; // Value in cents
  onChange: (value: number) => void; // Callback with value in cents
  currency?: string;
}

const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ className, value, onChange, currency = "$", ...props }, ref) => {
    // Keep track of the actual user input
    const [localValue, setLocalValue] = React.useState("");
    const [isFocused, setIsFocused] = React.useState(false);

    // Initialize and sync with external value when not focused
    React.useEffect(() => {
      if (!isFocused && value > 0) {
        const dollars = value / 100;
        // Only show decimals if they exist
        setLocalValue(
          dollars % 1 === 0 ? dollars.toString() : dollars.toFixed(2)
        );
      } else if (!isFocused && value === 0) {
        setLocalValue("");
      }
    }, [value, isFocused]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;

      // Allow empty string
      if (input === "") {
        setLocalValue("");
        onChange(0);
        return;
      }

      // Remove any non-numeric characters except decimal point
      const cleaned = input.replace(/[^\d.]/g, "");

      // Prevent multiple decimal points
      const parts = cleaned.split(".");
      let formatted = parts[0];
      if (parts.length > 1) {
        // Limit to 2 decimal places
        formatted += `.${parts[1].substring(0, 2)}`;
      }

      // Update local value
      setLocalValue(formatted);

      // Parse to number and convert to cents
      const numValue = parseFloat(formatted);
      if (!Number.isNaN(numValue)) {
        onChange(Math.round(numValue * 100));
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
      // Optionally format on blur if there's a valid number
      if (localValue && localValue !== "") {
        const num = parseFloat(localValue);
        if (!Number.isNaN(num) && num > 0) {
          // Only show decimals if they exist
          setLocalValue(num % 1 === 0 ? num.toString() : num.toFixed(2));
        }
      }
    };

    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
          {currency}
        </span>
        <input
          ref={ref}
          type="text"
          inputMode="decimal"
          value={localValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background pl-8 pr-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            className
          )}
          placeholder="0.00"
          {...props}
        />
      </div>
    );
  }
);

CurrencyInput.displayName = "CurrencyInput";

export { CurrencyInput };
